/**
 * Google Reviews Trust Badge - Glowing style
 * Drives customer confidence for mail-in services
 */
import React from 'react';

const GOOGLE_REVIEWS_URL = 'https://share.google/9RAR40lhoe1RijZzZ';
const REVIEW_COUNT = 402;
const RATING = 5.0;

const GoogleReviewsBadge = ({ variant = 'default', className = '' }) => {
  const stars = Array.from({ length: 5 }, (_, i) => (
    <svg key={i} className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-yellow-400 fill-yellow-400" viewBox="0 0 20 20">
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
  ));

  const googleIcon = (
    <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );

  if (variant === 'compact') {
    return (
      <a
        href={GOOGLE_REVIEWS_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={`group inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-slate-800/80 border border-yellow-500/20 hover:border-yellow-500/50 transition-all duration-300 hover:shadow-[0_0_20px_rgba(234,179,8,0.15)] ${className}`}
        data-testid="google-reviews-badge-compact"
      >
        {googleIcon}
        <div className="flex items-center gap-0.5">{stars}</div>
        <span className="text-yellow-400/90 font-semibold text-sm">{RATING}</span>
        <span className="text-slate-400 text-xs">({REVIEW_COUNT}+)</span>
      </a>
    );
  }

  // Light variant for pages with bright backgrounds
  if (variant === 'light') {
    return (
      <a
        href={GOOGLE_REVIEWS_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={`group relative inline-flex rounded-full overflow-hidden transition-all duration-500 hover:scale-[1.02] ${className}`}
        data-testid="google-reviews-badge"
      >
        <div className="absolute -inset-[1px] rounded-full bg-gradient-to-r from-yellow-400/60 via-amber-300/40 to-yellow-400/60 opacity-60 group-hover:opacity-100 transition-opacity duration-500 animate-[glow-pulse_3s_ease-in-out_infinite] blur-[2px]" />
        <div className="relative bg-white/95 backdrop-blur-sm rounded-full px-5 py-2.5 sm:px-6 sm:py-3 border border-yellow-400/30 group-hover:border-yellow-400/60 transition-all duration-300 shadow-md shadow-yellow-500/10 flex items-center gap-2.5 sm:gap-3">
          {googleIcon}
          <div className="flex items-center gap-0.5">{stars}</div>
          <span className="text-yellow-600 font-bold text-sm sm:text-base">{RATING}</span>
          <div className="w-px h-4 bg-slate-300" />
          <span className="text-slate-700 font-semibold text-xs sm:text-sm">{REVIEW_COUNT}+ Reviews</span>
          <svg className="w-3.5 h-3.5 text-slate-400 group-hover:text-yellow-600 group-hover:translate-x-0.5 transition-all" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25" />
          </svg>
        </div>
        <style>{`
          @keyframes glow-pulse {
            0%, 100% { opacity: 0.4; }
            50% { opacity: 0.85; }
          }
        `}</style>
      </a>
    );
  }

  return (
    <a
      href={GOOGLE_REVIEWS_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`group relative inline-flex rounded-full overflow-hidden transition-all duration-500 hover:scale-[1.02] ${className}`}
      data-testid="google-reviews-badge"
    >
      {/* Glow border */}
      <div className="absolute -inset-[1px] rounded-full bg-gradient-to-r from-yellow-500/50 via-amber-400/30 to-yellow-500/50 opacity-50 group-hover:opacity-100 transition-opacity duration-500 animate-[glow-pulse_3s_ease-in-out_infinite] blur-[2px]" />
      
      <div className="relative bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-full px-5 py-2.5 sm:px-6 sm:py-3 border border-yellow-500/20 group-hover:border-yellow-500/50 transition-all duration-300 flex items-center gap-2.5 sm:gap-3">
        {googleIcon}
        <div className="flex items-center gap-0.5">{stars}</div>
        <span className="text-yellow-400 font-bold text-sm sm:text-base">{RATING}</span>
        <div className="w-px h-4 bg-slate-600" />
        <span className="text-white font-semibold text-xs sm:text-sm">{REVIEW_COUNT}+ Reviews</span>
        <svg className="w-3.5 h-3.5 text-slate-400 group-hover:text-yellow-400 group-hover:translate-x-0.5 transition-all" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25" />
        </svg>
      </div>

      <style>{`
        @keyframes glow-pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.85; }
        }
      `}</style>
    </a>
  );
};

export default GoogleReviewsBadge;
